/**
 * Support Tickets Management Page for GEMS Admin Portal
 * Allows admins to view, respond to, and manage user support tickets
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../AdminApp';
import {
  Ticket,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Search,
  Filter,
  RefreshCw,
  Send,
  User,
  ChevronDown,
  Bell,
} from 'lucide-react';

// Ticket categories
const TICKET_CATEGORIES = [
  { id: 'all', label: 'All Categories' },
  { id: 'technical', label: 'Technical Issue' },
  { id: 'account', label: 'Account' },
  { id: 'billing', label: 'Billing' },
  { id: 'feedback', label: 'Feedback' },
  { id: 'report', label: 'Report User' },
  { id: 'gameplay', label: 'Gameplay' },
  { id: 'other', label: 'Other' },
];

// Ticket statuses
const TICKET_STATUSES = [
  { id: 'all', label: 'All Status', color: 'gray' },
  { id: 'open', label: 'Open', color: 'green' },
  { id: 'in_progress', label: 'In Progress', color: 'amber' },
  { id: 'resolved', label: 'Resolved', color: 'blue' },
  { id: 'closed', label: 'Closed', color: 'gray' },
];

const SupportTicketsPage = () => {
  const { api, admin } = useAdmin();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalTickets, setTotalTickets] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [needsHumanOnly, setNeedsHumanOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Selected ticket for detail view
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    open: 0,
    in_progress: 0,
    resolved: 0,
    closed: 0,
    needs_human: 0,
  });

  // Fetch tickets
  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (needsHumanOnly) params.needs_human = true;

      const response = await api.get('/api/admin/support/tickets', { params });
      setTickets(response.data.tickets || []);
      setTotalTickets(response.data.total || 0);
      
      // Calculate stats from tickets
      const allTicketsResponse = await api.get('/api/admin/support/tickets', { 
        params: { page: 1, page_size: 1000 } 
      });
      const allTickets = allTicketsResponse.data.tickets || [];
      setStats({
        open: allTickets.filter(t => t.status === 'open').length,
        in_progress: allTickets.filter(t => t.status === 'in_progress').length,
        resolved: allTickets.filter(t => t.status === 'resolved').length,
        closed: allTickets.filter(t => t.status === 'closed').length,
        needs_human: allTickets.filter(t => t.needs_human && (t.status === 'open' || t.status === 'in_progress')).length,
      });
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [currentPage, statusFilter, categoryFilter, needsHumanOnly]);

  // Respond to ticket
  const handleRespond = async () => {
    if (!responseText.trim() || !selectedTicket) return;
    
    setSubmitting(true);
    try {
      await api.post(`/api/admin/support/tickets/${selectedTicket.id}/respond`, {
        response: responseText,
        status: newStatus || 'in_progress',
      });
      
      // Refresh ticket details
      const response = await api.get(`/api/admin/support/tickets/${selectedTicket.id}`, {
        params: { admin: true }
      });
      
      // Update local state
      setSelectedTicket(prev => ({
        ...prev,
        responses: [...(prev.responses || []), {
          admin_id: admin.id,
          admin_name: admin.name,
          response: responseText,
          timestamp: new Date().toISOString(),
        }],
        status: newStatus || 'in_progress',
      }));
      
      setResponseText('');
      setNewStatus('');
      fetchTickets(); // Refresh list
    } catch (error) {
      console.error('Error responding to ticket:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Update ticket status
  const updateTicketStatus = async (ticketId, status) => {
    try {
      await api.put(`/api/admin/support/tickets/${ticketId}`, { status });
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, status }));
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'text-green-600 bg-green-50 border-green-200';
      case 'in_progress': return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'resolved': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'closed': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertCircle className="w-4 h-4" />;
      case 'in_progress': return <Clock className="w-4 h-4" />;
      case 'resolved': return <CheckCircle className="w-4 h-4" />;
      case 'closed': return <XCircle className="w-4 h-4" />;
      default: return <Ticket className="w-4 h-4" />;
    }
  };

  // Get category label
  const getCategoryLabel = (categoryId) => {
    const category = TICKET_CATEGORIES.find(c => c.id === categoryId);
    return category ? category.label : categoryId;
  };

  // Filter tickets by search
  const filteredTickets = tickets.filter(ticket => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      ticket.subject?.toLowerCase().includes(query) ||
      ticket.username?.toLowerCase().includes(query) ||
      ticket.message?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Support Tickets</h1>
          <p className="text-gray-500">Topaz answers routine tickets automatically; anything marked <span className="font-medium text-amber-700">Needs a person</span> is waiting on you.</p>
        </div>
        <button
          onClick={fetchTickets}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
          data-testid="refresh-tickets-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div
          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${needsHumanOnly ? 'border-amber-500 bg-amber-50' : 'border-gray-200 bg-white hover:border-amber-300'}`}
          onClick={() => setNeedsHumanOnly(v => !v)}
          data-testid="stat-needs-human"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Needs a person</p>
              <p className="text-2xl font-bold text-amber-700">{stats.needs_human}</p>
            </div>
            <div className="p-3 bg-amber-100 rounded-lg">
              <Bell className="w-6 h-6 text-amber-700" />
            </div>
          </div>
        </div>

        <div
          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${statusFilter === 'open' ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-green-300'}`}
          onClick={() => setStatusFilter(statusFilter === 'open' ? 'all' : 'open')}
          data-testid="stat-open"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Open</p>
              <p className="text-2xl font-bold text-green-600">{stats.open}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <AlertCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div 
          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${statusFilter === 'in_progress' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 bg-white hover:border-amber-300'}`}
          onClick={() => setStatusFilter(statusFilter === 'in_progress' ? 'all' : 'in_progress')}
          data-testid="stat-in-progress"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">In Progress</p>
              <p className="text-2xl font-bold text-amber-600">{stats.in_progress}</p>
            </div>
            <div className="p-3 bg-amber-100 rounded-lg">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </div>

        <div 
          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${statusFilter === 'resolved' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300'}`}
          onClick={() => setStatusFilter(statusFilter === 'resolved' ? 'all' : 'resolved')}
          data-testid="stat-resolved"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Resolved</p>
              <p className="text-2xl font-bold text-blue-600">{stats.resolved}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div 
          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${statusFilter === 'closed' ? 'border-gray-500 bg-gray-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
          onClick={() => setStatusFilter(statusFilter === 'closed' ? 'all' : 'closed')}
          data-testid="stat-closed"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Closed</p>
              <p className="text-2xl font-bold text-gray-600">{stats.closed}</p>
            </div>
            <div className="p-3 bg-gray-100 rounded-lg">
              <XCircle className="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search tickets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid="search-tickets-input"
          />
        </div>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="category-filter"
        >
          {TICKET_CATEGORIES.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.label}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="status-filter"
        >
          {TICKET_STATUSES.map(status => (
            <option key={status.id} value={status.id}>{status.label}</option>
          ))}
        </select>
      </div>

      {/* Main Content */}
      <div className="flex gap-6">
        {/* Tickets List */}
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <Ticket className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No tickets found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  onClick={() => setSelectedTicket(ticket)}
                  className={`p-4 bg-white rounded-xl border-2 cursor-pointer transition-all hover:shadow-md ${
                    selectedTicket?.id === ticket.id ? 'border-emerald-500' : 'border-gray-200 hover:border-emerald-300'
                  }`}
                  data-testid={`ticket-row-${ticket.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-800 truncate">{ticket.subject}</h3>
                        {ticket.responses?.length > 0 && (
                          <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                            <MessageSquare className="w-3 h-3" />
                            {ticket.responses.length}
                          </span>
                        )}
                        {ticket.needs_human && (ticket.status === 'open' || ticket.status === 'in_progress') && (
                          <span className="text-[10px] font-semibold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full" data-testid="needs-human-badge">
                            Needs a person
                          </span>
                        )}
                        {!ticket.needs_human && ticket.responses?.some(r => r.admin_id === 'topaz') && (
                          <span className="text-[10px] font-medium text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full">
                            Topaz answered
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-500 mb-2">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {ticket.username}
                        </span>
                        <span className="text-emerald-600">{getCategoryLabel(ticket.category)}</span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{ticket.message}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(ticket.status)}`}>
                        {getStatusIcon(ticket.status)}
                        {ticket.status?.replace('_', ' ')}
                      </span>
                      <span className="text-xs text-gray-400">{formatDate(ticket.created_at)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalTickets > pageSize && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500">
                Page {currentPage} of {Math.ceil(totalTickets / pageSize)}
              </span>
              <button
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage >= Math.ceil(totalTickets / pageSize)}
                className="px-3 py-1 text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Ticket Detail Panel */}
        {selectedTicket && (
          <div className="w-[450px] bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col max-h-[calc(100vh-200px)]">
            {/* Detail Header */}
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800">{selectedTicket.subject}</h3>
                  <p className="text-sm text-gray-500">
                    {selectedTicket.username} • {formatDate(selectedTicket.created_at)}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <XCircle className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              
              {/* Status and Category */}
              <div className="flex items-center gap-2 mt-3">
                <span className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(selectedTicket.status)}`}>
                  {getStatusIcon(selectedTicket.status)}
                  {selectedTicket.status?.replace('_', ' ')}
                </span>
                <span className="px-2 py-1 text-xs font-medium text-emerald-600 bg-emerald-50 rounded-full">
                  {getCategoryLabel(selectedTicket.category)}
                </span>
              </div>
            </div>

            {/* Message and Responses */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Original Message */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600 mb-2 font-medium">Original Message:</p>
                <p className="text-sm text-gray-800">{selectedTicket.message}</p>
                {selectedTicket.attachment_url && (
                  <a
                    href={selectedTicket.attachment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-2 text-sm text-emerald-600 hover:underline"
                  >
                    View Attachment
                  </a>
                )}
              </div>

              {/* Responses */}
              {selectedTicket.needs_human && (selectedTicket.status === 'open' || selectedTicket.status === 'in_progress') && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900" data-testid="needs-human-panel">
                  <p className="font-semibold">Topaz handed this ticket to a person.</p>
                  <p className="mt-1">{selectedTicket.escalation_reason || 'This needs human verification.'} Your reply below goes to the member and clears the hand-off.</p>
                </div>
              )}
              {selectedTicket.responses?.map((response, index) => {
                const isTopaz = response.admin_id === 'topaz' || String(response.from || '').startsWith('TOPAZ');
                return (
                  <div key={index} className={`rounded-lg p-3 border-l-4 ${isTopaz ? 'bg-amber-50 border-amber-400' : 'bg-emerald-50 border-emerald-500'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-sm font-medium ${isTopaz ? 'text-amber-700' : 'text-emerald-700'}`}>
                        {response.admin_name || response.from || 'Support'}
                        {isTopaz && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">AI</span>}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatDate(response.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-800 whitespace-pre-line">{response.response ?? response.message}</p>
                  </div>
                );
              })}
            </div>

            {/* Response Form */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">Update Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="update-status-select"
                >
                  <option value="">Keep current status</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              
              <textarea
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                placeholder="Write your response..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                data-testid="response-textarea"
              />
              
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleRespond}
                  disabled={!responseText.trim() || submitting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="send-response-btn"
                >
                  <Send className="w-4 h-4" />
                  {submitting ? 'Sending...' : 'Send Response'}
                </button>
              </div>
              
              {/* Quick Actions */}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => updateTicketStatus(selectedTicket.id, 'resolved')}
                  className="flex-1 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  data-testid="mark-resolved-btn"
                >
                  Mark Resolved
                </button>
                <button
                  onClick={() => updateTicketStatus(selectedTicket.id, 'closed')}
                  className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  data-testid="close-ticket-btn"
                >
                  Close Ticket
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportTicketsPage;
